import React, { useState } from 'react';
import { createPartner, updatePartner } from '../../services/api';
import toast from 'react-hot-toast';

const PartnerForm = ({ editingItem, categories, onClose, onRefresh }) => {
  const [formData, setFormData] = useState({
    name: editingItem?.name || '',
    type: editingItem?.type || 'Technology',
    category: editingItem?.category || '',
    website: editingItem?.website || '',
    description: editingItem?.description || '',
    logo: editingItem?.logo || '',
    order: editingItem?.order || 0,
    isActive: editingItem?.isActive !== false
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(editingItem?.logo || '');
  const [loading, setLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'https://inteliheads.in/api';

  const partnerTypes = [
    { value: 'Technology', label: 'Technology Partner' },
    { value: 'Strategic', label: 'Strategic Partner' },
    { value: 'Channel', label: 'Channel Partner' },
    { value: 'Implementation', label: 'Implementation Partner' },
    { value: 'Reseller', label: 'Reseller Partner' },
    { value: 'Consulting', label: 'Consulting Partner' }
  ];

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImageToS3 = async () => {
    if (!imageFile) return formData.logo;
    
    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', imageFile);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/upload/s3/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Logo uploaded successfully');
        return result.data.url;
      } else {
        toast.error(result.message || 'Upload failed');
        return formData.logo;
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading logo');
      return formData.logo;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    setLoading(true);
    
    try {
      let logo = formData.logo;
      if (imageFile) {
        logo = await uploadImageToS3();
      }
      
      const submitData = {
        name: formData.name,
        type: formData.type,
        category: formData.category,
        website: formData.website,
        description: formData.description,
        logo: logo,
        order: parseInt(formData.order) || 0,
        isActive: formData.isActive
      };
      
      if (editingItem && editingItem._id) {
        await updatePartner(editingItem._id, submitData);
        toast.success('Partner updated successfully');
      } else {
        await createPartner(submitData);
        toast.success('Partner created successfully');
      }
      
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving partner:', error);
      toast.error(error.message || 'Error saving partner');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000 
    }} onClick={onClose}>
      <div style={{ 
        background: 'white', 
        borderRadius: '16px', 
        padding: '2rem', 
        maxWidth: '650px', 
        width: '90%', 
        maxHeight: '85vh', 
        overflow: 'auto' 
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>
            {editingItem ? 'Edit Partner' : 'Add New Partner'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* Partner Name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Partner Name *</label>
            <input 
              type="text" 
              required 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              placeholder="Enter partner organization name"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Partner Type and Category */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Partner Type *</label>
              <select 
                value={formData.type} 
                onChange={(e) => setFormData({ ...formData, type: e.target.value })} 
                required
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
              >
                {partnerTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Category *</label>
              <select 
                value={formData.category} 
                onChange={(e) => setFormData({ ...formData, category: e.target.value })} 
                required
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
              >
                <option value="">-- Select a category --</option>
                {categories.filter(cat => cat.type === 'partner').map(cat => (
                  <option key={cat._id || cat.name} value={cat.name}>
                    {cat.icon && <span style={{ marginRight: '0.5rem' }}>{cat.icon}</span>}
                    {cat.name}
                  </option>
                ))}
              </select>
              {categories.filter(cat => cat.type === 'partner').length === 0 && (
                <p style={{ color: '#F5A623', fontSize: '0.7rem', marginTop: '0.25rem' }}>
                  <i className="fas fa-info-circle"></i> No partner categories found. Please add categories first.
                </p>
              )}
            </div>
          </div>
          
          {/* Website */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Website</label>
            <input 
              type="url" 
              value={formData.website} 
              onChange={(e) => setFormData({ ...formData, website: e.target.value })} 
              placeholder="https://example.com"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Description</label>
            <textarea 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
              rows="3" 
              placeholder="Partner description"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Order and Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Display Order</label>
              <input 
                type="number" 
                value={formData.order} 
                onChange={(e) => setFormData({ ...formData, order: e.target.value })} 
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
              <small style={{ color: '#5A6E7F' }}>Lower numbers appear first</small>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={formData.isActive} 
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} 
                />
                <span>Active</span>
              </label>
            </div>
          </div>
          
          {/* Partner Logo */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Partner Logo</label>
            
            {imagePreview && (
              <div style={{ marginBottom: '0.5rem' }}>
                <img 
                  src={imagePreview.startsWith('http') ? imagePreview : getFullImageUrl(imagePreview)} 
                  alt="Preview" 
                  style={{ 
                    width: '80px', 
                    height: '80px', 
                    objectFit: 'contain', 
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    padding: '0.25rem'
                  }} 
                />
              </div>
            )}
            
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: '#2C7A7B',
                color: 'white',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}>
                <i className="fas fa-upload"></i> Choose Logo
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageChange} 
                  style={{ display: 'none' }} 
                />
              </label>
              <small style={{ color: '#5A6E7F', marginLeft: '0.5rem' }}>
                Max 5MB, JPG, PNG, GIF, WEBP
              </small>
            </div>
            
            <input 
              type="text" 
              value={formData.logo} 
              onChange={(e) => {
                setFormData({ ...formData, logo: e.target.value });
                setImagePreview(e.target.value);
                setImageFile(null);
              }} 
              placeholder="Or enter logo URL directly" 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Submit Buttons */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button 
              type="submit" 
              disabled={loading || uploading} 
              style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#2C7A7B', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontWeight: 600,
                opacity: (loading || uploading) ? 0.7 : 1
              }}
            >
              {loading || uploading ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                <><i className="fas fa-save"></i> Save Partner</>
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#5A6E7F', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontWeight: 600 
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartnerForm;